import { Loader2, PhoneMissed, PhoneOff, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    getBlockedPhoneErrorMessage,
    isSoftBlockCode,
} from '@apps/web/lib/blocked_phone_utils';
import { type BlockedCaregiverInfo } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type BlockedPhonePopoverProps = {
    readonly blockedInfos: ReadonlyArray<BlockedCaregiverInfo>;
    readonly onUnblocked: () => void;
};

const CLOSE_DELAY_MS = 500;

export function BlockedPhonePopover({
    blockedInfos,
    onUnblocked,
}: BlockedPhonePopoverProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [unblockingPhone, setUnblockingPhone] = useState<string | null>(null);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const organizationId = useOrganizationId();

    const clearCloseTimeout = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    const startCloseTimeout = () => {
        clearCloseTimeout();
        closeTimeoutRef.current = setTimeout(() => {
            if (!unblockingPhone) {
                setIsOpen(false);
            }
        }, CLOSE_DELAY_MS);
    };

    const handleMouseEnter = () => {
        clearCloseTimeout();
    };

    const handleMouseLeave = () => {
        startCloseTimeout();
    };

    useEffect(() => {
        return () => clearCloseTimeout();
    }, []);

    const handleUnblock = async (info: BlockedCaregiverInfo) => {
        if (!organizationId) {
            return;
        }
        setUnblockingPhone(info.phone_number);
        try {
            await getApiClient().post({
                url: '/organizations/{organization_id}/blocked-phones/unblock-caregiver',
                pathParams: { organization_id: organizationId },
                body: {
                    caregiver_id: info.caregiver_id,
                    phone_number: info.phone_number,
                },
            });
            toast.success('Phone number marked as not blocked', {
                persistNotification: false,
            });
            onUnblocked();
        } catch (error) {
            console.error('Failed to unblock caregiver:', error);
            toast.error('Failed to mark phone number as not blocked', {
                persistNotification: false,
            });
        } finally {
            setUnblockingPhone(null);
            if (blockedInfos.length <= 1) {
                setIsOpen(false);
            }
        }
    };

    if (blockedInfos.length === 0) {
        return null;
    }

    // Check if all blocks are soft blocks (temporary issues)
    const hasHardBlock = blockedInfos.some(
        (info) => !isSoftBlockCode(info.error_code),
    );

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild={true}>
                <button
                    type="button"
                    className="cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {hasHardBlock ? (
                        <PhoneOff className="h-4 w-4 text-red-500" />
                    ) : (
                        <PhoneMissed className="h-4 w-4 text-amber-500" />
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="w-80"
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <button
                    type="button"
                    className="absolute top-2 right-2 cursor-pointer rounded-sm opacity-70"
                    onClick={() => setIsOpen(false)}
                >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </button>
                <div className="space-y-4">
                    {blockedInfos.map((info) => (
                        <div
                            key={info.phone_number}
                            className="space-y-2 border-b pb-3 last:border-b-0 last:pb-0"
                        >
                            <p className="text-muted-foreground text-sm">
                                {getBlockedPhoneErrorMessage(
                                    info.error_code,
                                    info.provider,
                                )}
                            </p>
                            <div className="flex justify-end">
                                <Tooltip>
                                    <TooltipTrigger asChild={true}>
                                        <Button
                                            size="sm"
                                            variant={
                                                isSoftBlockCode(info.error_code)
                                                    ? 'outline'
                                                    : 'destructive'
                                            }
                                            className={
                                                isSoftBlockCode(info.error_code)
                                                    ? 'border-amber-500 text-amber-600 hover:bg-amber-50 hover:text-amber-700'
                                                    : undefined
                                            }
                                            onClick={() => handleUnblock(info)}
                                            disabled={unblockingPhone !== null}
                                        >
                                            {unblockingPhone ===
                                            info.phone_number ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : isSoftBlockCode(
                                                  info.error_code,
                                              ) ? (
                                                'Dismiss issue'
                                            ) : (
                                                `Mark ${info.phone_number} as not blocked`
                                            )}
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[250px]">
                                        <p>
                                            {isSoftBlockCode(info.error_code)
                                                ? 'Dismiss this temporary issue. Phoebe will attempt to reach this caregiver dismissed or not.'
                                                : "Mark this number as no longer blocked if you've reached out to this person and asked them to do so. Phoebe cannot remove the block they requested with their carrier directly."}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
